import type { IntlShape } from 'react-intl'

import type { AnswerMetadata, ConsentContext, ConsentSubjectContext } from './consentOptions/types'

export type ConsentSubjectKind = 'self' | 'proband' | 'sibling'

export type ConsentSubject = ConsentSubjectContext & { kind: ConsentSubjectKind }

type ConsentParty = NonNullable<ConsentContext['consentParties']>[number]

/**
 * The server lists only the proband, guardian-covered Siblings and the consenter, so a subject
 * that is neither the consenter nor the proband is treated as a Sibling.
 */
function subjectKind(subject: ConsentSubjectContext, party: ConsentParty | undefined): ConsentSubjectKind {
  if (party?.isPrimaryGuardian) return 'self'
  const relationship = (subject.relationship ?? party?.relationship ?? '').trim().toLowerCase()
  return relationship === 'proband' ? 'proband' : 'sibling'
}

/** Subjects in the order the server sent them, or null when the context has no subject list. */
export function getConsentSubjects(context: ConsentContext | null | undefined): ConsentSubject[] | null {
  const raw = context?.consentSubjects
  if (!Array.isArray(raw)) return null
  const parties = new Map((context?.consentParties ?? []).map((party) => [party.familyMemberId, party]))
  const subjects = raw
    .filter((subject) => typeof subject?.familyMemberId === 'string' && subject.familyMemberId.trim() !== '')
    .map((subject) => ({ ...subject, kind: subjectKind(subject, parties.get(subject.familyMemberId)) }))
  return subjects.length > 0 ? subjects : null
}

export function hasSiblingSubject(subjects: ConsentSubject[] | null): boolean {
  return subjects?.some((subject) => subject.kind === 'sibling') ?? false
}

/** Subjects whose Consent to Test the guardian signs here; the sentence and the echo both use it. */
export function signedSubjects(subjects: ConsentSubject[] | null): ConsentSubject[] {
  return (subjects ?? []).filter((subject) => subject.collectConsentToTest === true)
}

/** Ids for the `subjectFamilyMemberIds` echo on the Consent to Test save. */
export function consentToTestSubjectIds(subjects: ConsentSubject[] | null): string[] {
  return signedSubjects(subjects).map((subject) => subject.familyMemberId)
}

/**
 * Subjects that get their own secondary answer row, or null when no guardian-covered Sibling is
 * listed. Outside dual mode the server keeps no separate answer for the consenter. In dual mode
 * the consenter always answers, even when their own line is missing from the list.
 */
export function secondaryAnswerSubjects(
  context: ConsentContext | null | undefined,
  dual: boolean
): ConsentSubject[] | null {
  const subjects = getConsentSubjects(context)
  if (!subjects || !hasSiblingSubject(subjects)) return null
  const rows = subjects.filter((subject) => subject.kind !== 'self' || dual)
  const guardian = context?.consentParties?.find((party) => party.isPrimaryGuardian)
  if (dual && guardian && !rows.some((subject) => subject.kind === 'self')) {
    rows.push({ familyMemberId: guardian.familyMemberId, kind: 'self' })
  }
  return rows
}

/** Where a Sibling's answer lives in the secondary answers map. */
export function siblingStateKey(itemId: string, siblingId: string): string {
  return `${itemId}:${siblingId}`
}

/** Joins the shared secondary answers (or their metadata) with each Sibling's, keyed like the rows. */
export function withSiblingAnswers<T>(
  shared: Record<string, T> | undefined,
  perSibling: Record<string, Record<string, unknown>> | undefined,
  isValue: (value: unknown) => value is T
): Record<string, T> {
  const answers: Record<string, T> = { ...(shared ?? {}) }
  for (const [siblingId, values] of Object.entries(perSibling ?? {})) {
    for (const [itemId, value] of Object.entries(values ?? {})) {
      if (isValue(value)) answers[siblingStateKey(itemId, siblingId)] = value
    }
  }
  return answers
}

export function isBooleanAnswer(value: unknown): value is boolean {
  return typeof value === 'boolean'
}

export function isAnswerMetadata(value: unknown): value is AnswerMetadata {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  const { updatedAt } = value as { updatedAt?: unknown }
  return updatedAt === undefined || updatedAt === null || typeof updatedAt === 'string'
}

/** Key of a shared answer in `itemStates`: `item`, `item:SELF` or `item:DEPENDENT`. */
export function scopedStateKey(itemId: string, scope?: 'SELF' | 'DEPENDENT'): string {
  return scope ? `${itemId}:${scope}` : itemId
}

/** Key in the secondary answers map, which joins `itemStates` with the per-Sibling answers. */
export function subjectStateKey(itemId: string, subject: ConsentSubject, dual: boolean): string {
  if (subject.kind === 'sibling') return siblingStateKey(itemId, subject.familyMemberId)
  if (subject.kind === 'self') return scopedStateKey(itemId, 'SELF')
  return scopedStateKey(itemId, dual ? 'DEPENDENT' : undefined)
}

/** One secondary answer row: the subject and where its answer lives in the answers map. */
export type SubjectRow = { subject: ConsentSubject; key: string }

export function subjectRows(itemId: string, subjects: ConsentSubject[], dual: boolean): SubjectRow[] {
  return subjects.map((subject) => ({ subject, key: subjectStateKey(itemId, subject, dual) }))
}

/** How a saved secondary entry names its subject: a legacy scope, a family member id, or nothing. */
export type SubjectTarget = { subjectScope?: 'SELF' | 'DEPENDENT'; subjectFamilyMemberId?: string }

export type AnswerSubjectTab = {
  key: string
  subject: ConsentSubject
  /** Where this subject's answer for an item lives in the answers map. */
  stateKey: (itemId: string) => string
  target: SubjectTarget
}

/** One Manage Consents tab per secondary answer subject, or null when no Sibling is listed. */
export function answerSubjectTabs(
  context: ConsentContext | null | undefined,
  dual: boolean
): AnswerSubjectTab[] | null {
  const subjects = secondaryAnswerSubjects(context, dual)
  if (!subjects) return null
  return subjects.map((subject) => ({
    key: subject.familyMemberId,
    subject,
    stateKey: (itemId: string) => subjectStateKey(itemId, subject, dual),
    target: { subjectFamilyMemberId: subject.familyMemberId },
  }))
}

/** Full name; a blank-named proband falls back to the context's patient name. Null when unknown. */
export function subjectDisplayName(subject: ConsentSubject, context: ConsentContext | null | undefined): string | null {
  const name = [subject.firstName, subject.lastName].filter(Boolean).join(' ').trim()
  if (name) return name
  if (subject.kind !== 'proband') return null
  return context?.patientName?.trim() || context?.patientFirstName?.trim() || null
}

/** Row legend: "For you", "For {name}", or "For your child" when the name is unknown. */
export function subjectLegend(
  subject: ConsentSubject,
  intl: IntlShape,
  context: ConsentContext | null | undefined
): string {
  if (subject.kind === 'self') {
    return intl.formatMessage({ id: 'consentOptions.secondaryChoice.forYou', defaultMessage: 'For you' })
  }
  const name = subjectDisplayName(subject, context)
  return name
    ? intl.formatMessage({ id: 'consentOptions.secondaryChoice.forPatient', defaultMessage: 'For {name}' }, { name })
    : intl.formatMessage({ id: 'consentOptions.secondaryChoice.forYourChild', defaultMessage: 'For your child' })
}

/** Manage Consents tab name and eyebrow; `selfName` is the signed-in guardian's display name. */
export function subjectTabLabels(
  subject: ConsentSubject,
  intl: IntlShape,
  context: ConsentContext | null | undefined,
  selfName: string
): { name: string; eyebrow: string } {
  if (subject.kind === 'self') {
    return {
      name: selfName,
      eyebrow: intl.formatMessage({ id: 'manageConsents.subject.selfEyebrow', defaultMessage: 'Your preferences' }),
    }
  }
  const name = subjectDisplayName(subject, context)
  if (subject.kind === 'proband') {
    return {
      name: name ?? intl.formatMessage({ id: 'manageConsents.subject.patientFallback', defaultMessage: 'Patient' }),
      eyebrow: intl.formatMessage({
        id: 'manageConsents.subject.dependentEyebrow',
        defaultMessage: 'Patient - You manage these choices as guardian',
      }),
    }
  }
  return {
    name:
      name ??
      intl.formatMessage({ id: 'consentOptions.secondaryChoice.forYourChild', defaultMessage: 'For your child' }),
    eyebrow: intl.formatMessage({
      id: 'manageConsents.subject.siblingEyebrow',
      defaultMessage: 'You manage these choices as guardian',
    }),
  }
}
